<template>
  <v-card class="ps-card cp" flat>
    <div class="ps-card__head">
      <v-icon small>mdi-chart-box-outline</v-icon>
      Kosten &amp; Gewinn
      <span class="ps-card__hint">Verkaufspreise aus Shopify, Kosten aus den Druckvarianten — Produktionsdaten werden nicht verändert</span>
      <v-spacer />
      <span v-if="doc.shopifySyncedAt" class="cp__synced">Preise {{ ago(doc.shopifySyncedAt) }}<template v-if="doc.salesSyncedAt"> · Verkäufe {{ ago(doc.salesSyncedAt) }}</template></span>
      <v-btn text small rounded class="mr-1" :loading="syncing === 'prices'" :disabled="!!syncing" @click="sync('prices')"><v-icon small left>mdi-tag-outline</v-icon>Preise laden</v-btn>
      <v-btn text small rounded class="mr-1" :loading="syncing === 'sales'" :disabled="!!syncing" @click="sync('sales')"><v-icon small left>mdi-cart-outline</v-icon>Verkäufe laden</v-btn>
      <v-btn text small rounded @click="showConfig = !showConfig"><v-icon small left>mdi-tune</v-icon>Annahmen</v-btn>
    </div>
    <v-card-text>
      <!-- Land (aus den Verkäufen; Grundländer immer dabei) -->
      <div class="cp__bar">
        <div class="cp__countries">
          <button v-for="cc in countries" :key="cc" type="button" class="cp__cc" :class="{ 'cp__cc--on': cc === country }" :title="unitsOf(cc) + ' Stück im Zeitraum'" @click="country = cc">
            {{ flag(cc) }} {{ cc }}<span v-if="unitsOf(cc)" class="cp__ccN">{{ unitsOf(cc) }}</span>
          </button>
        </div>
        <span class="cp__meta">MwSt {{ config.vat[country] || 0 }} % · Gebühr {{ config.paymentRate }} % + {{ Number(config.paymentFixed).toFixed(2) }} €</span>
        <v-spacer />
        <v-switch v-model="simulate" inset dense hide-details class="cp__sim" label="Preis-Simulation" />
      </div>

      <!-- Annahmen -->
      <div v-if="showConfig" class="cp__config">
        <div class="cp__configGrid">
          <div v-for="cc in countries" :key="'cfg-' + cc" class="cp__configRow">
            <span class="cp__configCc">{{ flag(cc) }} {{ cc }}</span>
            <v-text-field :value="config.vat[cc]" type="number" min="0" step="0.5" dense outlined hide-details suffix="% MwSt" class="cp__configField" @input="(v) => $set(config.vat, cc, Number(v))" />
            <v-select :value="config.countryShop[cc]" :items="shopKeys" dense outlined hide-details class="cp__configField" label="Shop (Rückfall)" @input="(v) => $set(config.countryShop, cc, v)" />
          </div>
        </div>
        <div class="cp__configRow cp__configRow--fee">
          <span class="cp__configCc">Zahlungsgebühr</span>
          <v-text-field v-model.number="config.paymentRate" type="number" min="0" step="0.1" dense outlined hide-details suffix="%" class="cp__configField" />
          <v-text-field v-model.number="config.paymentFixed" type="number" min="0" step="0.05" dense outlined hide-details suffix="€ fix" class="cp__configField" />
          <v-spacer />
          <v-btn small depressed rounded color="primary" :loading="savingConfig" @click="saveConfig">Annahmen speichern</v-btn>
        </div>
      </div>

      <!-- Simulation: Zusammenfassung + Übernahme -->
      <div v-if="simulate" class="cp__simBar">
        <div class="cp__simText">
          <b>Preis-Simulation</b> — vorbelegt mit den aktuellen Preisen in {{ country }}, einfach überschreiben. Marge neu und Δ Deckungsbeitrag beziehen sich auf die Stückzahl der letzten {{ salesDays || 30 }} Tage.
          <template v-if="simSummary.rows">
            <br /><b>{{ simSummary.rows }} Varianten</b> geändert · Δ je Stück im Schnitt {{ money(simSummary.avgDelta) }} · <b :class="simSummary.deltaContribution >= 0 ? 'cp__pos' : 'cp__neg'">Δ Deckungsbeitrag {{ money(simSummary.deltaContribution, 0) }} / {{ salesDays || 30 }} Tage</b>
          </template>
        </div>
        <v-btn text small rounded :disabled="!simSummary.rows" @click="clearSim">Auf aktuelle Preise zurücksetzen</v-btn>
        <v-btn v-if="isAdmin" small depressed rounded color="primary" :disabled="!simSummary.rows || applying" :loading="applying" @click="previewApply"><v-icon small left>mdi-cloud-upload-outline</v-icon>In Shopify übernehmen …</v-btn>
      </div>

      <v-data-table
        :headers="activeHeaders"
        :items="rows"
        :items-per-page="50"
        hide-default-footer
        sort-by="name"
        class="ps-table cp__table"
        :loading="loading"
      >
        <template v-slot:item.name="{ item }">
          <span class="ps-name">{{ item.name }}</span>
          <div class="ps-sub">{{ item.fulfiller || "kein Fulfiller" }}</div>
        </template>
        <template v-slot:item.gross="{ item }">
          <template v-if="item.gross !== null">
            <span v-if="item.compareAt" class="cp__strike">{{ item.compareAt.raw.toFixed(2) }}</span>
            <span class="ps-price">{{ item.currency === "EUR" ? money(item.gross) : `${item.raw.toFixed(2)} ${item.currency}` }}</span>
            <div class="ps-sub" :title="'Quelle: ' + item.priceSource">
              <template v-if="item.currency !== 'EUR'">≈ {{ money(item.gross) }} · </template>{{ item.priceSource }}
            </div>
          </template>
          <span v-else class="ps-chip ps-chip--muted">kein Preis</span>
        </template>
        <template v-slot:item.net="{ item }"><span v-if="item.net !== null">{{ money(item.net) }}</span><span v-else class="cp__muted">—</span></template>
        <template v-slot:item.costs="{ item }">
          <span>{{ money(item.costs) }}</span>
          <div class="ps-sub">{{ money(item.production) }} + {{ money(item.shipping) }} Versand + {{ money(item.employee) }} + {{ money(item.payment) }} Gebühr</div>
        </template>
        <template v-slot:item.margin="{ item }">
          <template v-if="item.margin !== null">
            <span class="cp__margin" :class="marginClass(item.marginPct)">{{ money(item.margin) }}</span>
            <div class="ps-sub">{{ item.marginPct.toFixed(0) }} %</div>
          </template>
          <span v-else class="cp__muted">—</span>
        </template>
        <template v-slot:item.units="{ item }"><span v-if="item.units">{{ item.units }}</span><span v-else class="cp__muted">—</span></template>
        <template v-slot:item.contribution="{ item }">
          <template v-if="item.contribution !== null">
            <span class="ps-price">{{ money(item.contribution, 0) }}</span>
            <div class="ps-sub">{{ money(item.grossEur, 0) }} Umsatz</div>
          </template>
          <span v-else class="cp__muted">—</span>
        </template>
        <!-- Simulation -->
        <template v-slot:item.testPrice="{ item }">
          <div class="cp__testFields">
            <v-text-field :value="sim[item.key] && sim[item.key].price" type="number" step="0.05" min="0" dense outlined hide-details :suffix="item.currency || 'EUR'" placeholder="Preis" class="cp__testField" @input="(v) => setSim(item.key, 'price', v)" />
            <v-text-field :value="sim[item.key] && sim[item.key].compareAt" type="number" step="0.05" min="0" dense outlined hide-details :suffix="item.currency || 'EUR'" placeholder="Streichpreis" class="cp__testField" @input="(v) => setSim(item.key, 'compareAt', v)" />
          </div>
        </template>
        <template v-slot:item.testMargin="{ item }">
          <template v-if="item.test">
            <span class="cp__margin" :class="marginClass(item.test.marginPct)">{{ money(item.test.margin) }}</span>
            <div class="ps-sub">{{ item.test.marginPct.toFixed(0) }} % · <span :class="item.test.deltaUnit >= 0 ? 'cp__pos' : 'cp__neg'">{{ item.test.deltaUnit >= 0 ? "+" : "" }}{{ money(item.test.deltaUnit) }}</span> je Stück</div>
          </template>
          <span v-else class="cp__muted">—</span>
        </template>
        <template v-slot:item.testContribution="{ item }">
          <span v-if="item.test && item.test.deltaContribution !== null" :class="item.test.deltaContribution >= 0 ? 'cp__pos' : 'cp__neg'">{{ item.test.deltaContribution >= 0 ? "+" : "" }}{{ money(item.test.deltaContribution, 0) }}</span>
          <span v-else class="cp__muted">—</span>
        </template>
        <template v-slot:footer>
          <div class="cp__foot">
            <span v-if="salesDays">Verkäufe: letzte {{ salesDays }} Tage, Land {{ country }}, Brutto nach Rabatt, ohne Extras. Länderpreise = vom Kunden gezahlter Listenpreis in seiner Währung (Shopify Markets); Streichpreis durchgestrichen davor.</span>
            <span v-else>Verkaufszahlen noch nicht geladen — „Verkäufe laden" holt die letzten 30 Tage aus allen Shops.</span>
          </div>
        </template>
      </v-data-table>

      <!-- Auswirkung protokollierter Preisänderungen -->
      <PriceChangeEffects :doc="doc" :print-variants="printVariants || []" :config="config" :employee-by-variant="employeeByVariant" />
    </v-card-text>

    <!-- Übernahme nach Shopify: Probelauf zeigen, dann bestätigen -->
    <v-dialog v-model="applyDialog" max-width="720" persistent>
      <v-card class="cp__applyCard">
        <v-card-title class="cp__applyTitle"><v-icon small class="mr-2">mdi-cloud-upload-outline</v-icon>Preise in Shopify ändern</v-card-title>
        <v-card-text>
          <p class="cp__applyHint">Alle Stile und Themen der Variante werden geändert. Die Änderung wird protokolliert.</p>
          <div v-for="s in applyPreview ? applyPreview.summary : []" :key="s.shopKey + (s.cc || '') + s.printVariantKey" class="cp__applyItem">
            <div class="cp__applyRow">
              <div class="cp__applyMain">
                <b>{{ variantName(s.printVariantKey) }}</b>
                <span class="cp__applyTarget">{{ s.target === "priceList" ? `Preisliste ${s.market}` : `Basispreis ${s.shopKey}` }}</span>
              </div>
              <div class="cp__applyPrice">
                <span class="cp__muted">{{ s.oldPrice.toFixed(2) }}</span> → <b>{{ s.newPrice.toFixed(2) }} {{ s.currency }}</b>
                <span v-if="s.newCompareAt !== null && s.newCompareAt !== undefined" class="cp__muted"> · Streichpreis {{ s.newCompareAt.toFixed(2) }}</span>
              </div>
            </div>
            <div class="cp__applyCountries">
              <span class="cp__applyLabel">Betroffene Länder</span>
              <span v-for="cc in s.countries" :key="cc" class="cp__applyCc">{{ flag(cc) }} {{ cc }}</span>
            </div>
            <details class="cp__applyProducts">
              <summary>{{ s.variants }} Shopify-Varianten in {{ s.products.length }} Produkten</summary>
              <div class="cp__applyProductList"><span v-for="t in s.products" :key="t">{{ t }}</span></div>
            </details>
          </div>
          <div v-if="applyError" class="cp__applyError"><v-icon small color="#c62828" class="mr-1">mdi-alert-circle-outline</v-icon>{{ applyError }}</div>
          <div v-if="applyPreview && marketRows().length" class="cp__applyError"><v-icon small color="#a86f00" class="mr-1">mdi-information-outline</v-icon>{{ marketRows().length }} Zeile(n) ohne Preisliste und ohne Shop-Währung werden nicht übernommen.</div>
          <v-checkbox v-if="applyNeedsForce" v-model="applyForce" dense hide-details label="Ich bestätige Änderungen über 50 %" class="mt-3" />
          <v-checkbox v-model="applyConfirmed" dense hide-details :label="`Ja, ${applyPreview ? applyPreview.variants : 0} Shopify-Varianten jetzt ändern`" class="mt-2" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text rounded :disabled="applying" @click="closeApply">Abbrechen</v-btn>
          <v-btn color="primary" depressed rounded :disabled="!applyPreview || !applyConfirmed || (applyNeedsForce && !applyForce) || applying" :loading="applying" @click="runApply">Jetzt ändern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { doc, getDoc, onSnapshot, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../../../../fb";
import { EventBus, events } from "../../../../../event-bus";
import { permissions } from "@/constants";
import { BASE_COUNTRIES, computeRow, mergeConfig } from "./pricing";
import PriceChangeEffects from "./PriceChangeEffects";

/** Übersicht Kosten & Gewinn je Druckvariante — liest printVariants/productTypes/skuMappings (unverändert) + settings/printVariantPrices */
export default {
  name: "CostProfitCard",
  components: { PriceChangeEffects },
  data() {
    return {
      country: "DE",
      doc: {},
      config: mergeConfig(null),
      rates: {},
      skuMappings: {},
      showConfig: false,
      syncing: "",
      savingConfig: false,
      loading: true,
      unsub: null,
      simulate: false,
      sim: {},
      applyDialog: false,
      applyPreview: null,
      applyError: "",
      applyNeedsForce: false,
      applyForce: false,
      applyConfirmed: false,
      applying: false,
      baseHeaders: [
        { text: "Variante", value: "name" },
        { text: "Preis brutto", value: "gross", align: "end" },
        { text: "Netto", value: "net", align: "end" },
        { text: "Kosten", value: "costs", align: "end" },
        { text: "Marge", value: "margin", align: "end" },
        { text: "Stück", value: "units", align: "end" },
        { text: "Deckungsbeitrag", value: "contribution", align: "end" },
      ],
      simHeaders: [
        { text: "Testpreis", value: "testPrice", sortable: false, width: 260 },
        { text: "Marge neu", value: "testMargin", align: "end", sortable: false },
        { text: "Δ Deckungsbeitrag", value: "testContribution", align: "end", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters(["printVariants", "productTypes", "userPermissions"]),
    isAdmin() {
      return (this.userPermissions || []).includes(permissions.ADMIN);
    },
    activeHeaders() {
      return this.simulate ? [...this.baseHeaders, ...this.simHeaders] : this.baseHeaders;
    },
    /** Grundländer + alle Länder mit Verkäufen, nach Stückzahl */
    countries() {
      const sold = this.doc.countries || {};
      const set = new Set([...BASE_COUNTRIES, ...Object.keys(sold)]);
      return [...set].sort((a, b) => (sold[b] || 0) - (sold[a] || 0) || a.localeCompare(b));
    },
    shopKeys() {
      const keys = new Set(Object.values(this.config.countryShop));
      for (const v of Object.values(this.doc.variants || {})) for (const k of Object.keys(v.shopify || {})) keys.add(k);
      return [...keys].sort();
    },
    salesDays() {
      return this.doc.sales && this.doc.sales.days ? this.doc.sales.days : 0;
    },
    employeeByVariant() {
      const out = {};
      const types = this.productTypes || [];
      const typeCost = (id) => {
        const t = types.find((x) => x.key === id || x.id === id);
        return t ? Number(t.costs) || 0 : 0;
      };
      for (const [sku, m] of Object.entries(this.skuMappings || {})) {
        if (!m || !m.printVariantId) continue;
        const live = /^(canvas|poster)_\d+x\d+$/.test(sku);
        if (out[m.printVariantId] === undefined || live) out[m.printVariantId] = typeCost(m.productTypeId);
      }
      return out;
    },
    rows() {
      const cc = this.country;
      const sales = (this.doc.sales && this.doc.sales.byVariant) || {};
      return (this.printVariants || [])
        .filter((v) => !v.isArchived && v.fulfiller && Object.keys(v.fulfiller).length)
        .map((v) =>
          computeRow({
            variant: v,
            entry: (this.doc.variants || {})[v.key],
            cc,
            config: this.config,
            rates: this.rates,
            employee: this.employeeByVariant[v.key],
            sales: sales[v.key] ? sales[v.key][cc] : null,
            testPrice: this.simulate && this.sim[v.key] ? this.sim[v.key].price : null,
            testCompareAt: this.simulate && this.sim[v.key] ? this.sim[v.key].compareAt : null,
          })
        );
    },
    /** Nur Zeilen, deren Testpreis oder Streichpreis vom aktuellen Stand abweicht */
    changedRows() {
      return this.rows.filter((r) => {
        if (!r.test || r.test.deltaUnit === null) return false;
        const priceChanged = Math.abs(r.test.raw - (r.raw || 0)) >= 0.005;
        const curCompare = r.compareAt ? r.compareAt.raw : null;
        const compareChanged = (r.test.compareAt || null) !== (curCompare || null);
        return priceChanged || compareChanged;
      });
    },
    simSummary() {
      const changed = this.changedRows;
      const deltaContribution = changed.reduce((s, r) => s + (r.test.deltaContribution || 0), 0);
      const avgDelta = changed.length ? changed.reduce((s, r) => s + r.test.deltaUnit, 0) / changed.length : 0;
      return { rows: changed.length, deltaContribution, avgDelta };
    },
  },
  async mounted() {
    this.loadPrintVariants();
    this.loadProductTypes();
    const [sku, stats] = await Promise.all([getDoc(doc(db, "settings", "skuMappings")), getDoc(doc(db, "settings", "statistics"))]);
    this.skuMappings = sku.exists() ? sku.data() : {};
    this.rates = (stats.exists() && stats.data().exchangeRates) || {};
    this.unsub = onSnapshot(doc(db, "settings", "printVariantPrices"), (snap) => {
      this.doc = snap.exists() ? snap.data() : {};
      if (!this.showConfig) this.config = mergeConfig(this.doc.config);
      this.loading = false;
    });
  },
  watch: {
    simulate(on) {
      if (on) this.prefillSim();
    },
    country() {
      if (this.simulate) this.prefillSim();
    },
  },
  beforeDestroy() {
    if (this.unsub) this.unsub();
  },
  methods: {
    ...mapActions(["loadPrintVariants", "loadProductTypes"]),
    flag(cc) {
      return /^[A-Z]{2}$/.test(cc) ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : "";
    },
    unitsOf(cc) {
      return (this.doc.countries || {})[cc] || 0;
    },
    variantName(key) {
      const v = (this.printVariants || []).find((x) => x.key === key);
      return v ? v.name : key;
    },
    money(v, digits = 2) {
      return `${Number(v || 0).toFixed(digits).replace(".", ",")} €`;
    },
    marginClass(pct) {
      if (pct === null || pct === undefined) return "";
      return pct >= 65 ? "cp__margin--good" : pct >= 45 ? "cp__margin--ok" : "cp__margin--low";
    },
    ago(ms) {
      const h = Math.round((Date.now() - ms) / 3600000);
      return h < 1 ? "gerade eben" : h < 48 ? `vor ${h} h` : `vor ${Math.round(h / 24)} Tagen`;
    },
    setSim(key, field, value) {
      this.$set(this.sim, key, { ...(this.sim[key] || {}), [field]: value });
    },
    clearSim() {
      this.prefillSim();
    },
    async sync(action) {
      this.syncing = action;
      try {
        const call = httpsCallable(functions, "printVariantPricesSync", { timeout: 540000 });
        const { data } = await call({ action, days: 30 });
        const n = action === "prices" ? data.prices && data.prices.variants : data.sales && data.sales.variants;
        EventBus.$emit(events.SNACKBAR, { message: `${action === "prices" ? "Preise" : "Verkäufe"} geladen: ${n} Varianten`, type: "success" });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Laden fehlgeschlagen: ${(e && e.message) || e}`, type: "error" });
      } finally {
        this.syncing = "";
      }
    },
    async saveConfig() {
      this.savingConfig = true;
      try {
        await setDoc(doc(db, "settings", "printVariantPrices"), { config: JSON.parse(JSON.stringify(this.config)) }, { merge: true });
        this.showConfig = false;
      } finally {
        this.savingConfig = false;
      }
    },
    /** Änderungen aus der Simulation: je Variante der Shop der Preisquelle, Preis in Shop-Währung */
    /** Nur Zeilen, deren Preisquelle in der Shop-Währung liegt — Markt-Preise (PLN, DKK …) sind eigene Preislisten */
    shopCurrency(shopKey) {
      for (const v of Object.values(this.doc.variants || {})) {
        const s = v.shopify && v.shopify[shopKey];
        if (s && s.currency) return s.currency;
      }
      return shopKey === "OH_US" ? "USD" : "EUR";
    },
    /** Übernehmbar: Markt mit eigener Preisliste (Preis in Marktwährung) ODER Basispreis in Shop-Währung */
    applicableRows() {
      return this.changedRows.filter((r) => r.shopKey && (r.market || (r.currency || "EUR") === this.shopCurrency(r.shopKey)));
    },
    marketRows() {
      return this.changedRows.filter((r) => r.shopKey && !r.market && (r.currency || "EUR") !== this.shopCurrency(r.shopKey));
    },
    applyChanges() {
      return this.applicableRows().map((r) => ({ printVariantKey: r.key, shopKey: r.shopKey, cc: r.market ? this.country : null, currency: r.currency || "EUR", price: r.test.raw, compareAt: r.test.compareAt }));
    },
    /** Simulation mit den aktuellen Preisen vorbelegen — nur abweichende Werte zählen als Änderung */
    prefillSim() {
      const sim = {};
      for (const r of this.rows) {
        if (r.raw === null || r.raw === undefined) continue;
        sim[r.key] = { price: Number(r.raw).toFixed(2), compareAt: r.compareAt ? Number(r.compareAt.raw).toFixed(2) : "" };
      }
      this.sim = sim;
    },
    async previewApply() {
      this.applyError = "";
      this.applyPreview = null;
      this.applyNeedsForce = false;
      this.applyForce = false;
      this.applyConfirmed = false;
      this.applyDialog = true;
      if (!this.applicableRows().length) {
        this.applyError = `Für ${this.country} gibt es weder eine eigene Preisliste noch läuft der Preis in Shop-Währung — bitte zuerst „Preise laden".`;
        return;
      }
      this.applying = true;
      try {
        const call = httpsCallable(functions, "printVariantPricesApply", { timeout: 540000 });
        const { data } = await call({ changes: this.applyChanges(), dryRun: true, force: false });
        this.applyPreview = data;
      } catch (e) {
        const msg = (e && e.message) || String(e);
        if (/50 %/.test(msg)) {
          this.applyNeedsForce = true;
          try {
            const call = httpsCallable(functions, "printVariantPricesApply", { timeout: 540000 });
            const { data } = await call({ changes: this.applyChanges(), dryRun: true, force: true });
            this.applyPreview = data;
            this.applyError = "Mindestens eine Änderung liegt über 50 % — bitte unten ausdrücklich bestätigen.";
          } catch (e2) {
            this.applyError = (e2 && e2.message) || String(e2);
          }
        } else {
          this.applyError = msg;
        }
      } finally {
        this.applying = false;
      }
    },
    async runApply() {
      this.applying = true;
      this.applyError = "";
      try {
        const call = httpsCallable(functions, "printVariantPricesApply", { timeout: 540000 });
        const { data } = await call({ changes: this.applyChanges(), dryRun: false, force: this.applyForce });
        const errs = (data.errors || []).length;
        EventBus.$emit(events.SNACKBAR, { message: `${data.updated} Shopify-Varianten geändert${errs ? `, ${errs} Fehler` : ""}`, type: errs ? "error" : "success" });
        this.applyDialog = false;
        this.sim = {};
      } catch (e) {
        this.applyError = (e && e.message) || String(e);
      } finally {
        this.applying = false;
      }
    },
    closeApply() {
      this.applyDialog = false;
      this.applyPreview = null;
    },
  },
};
</script>

<style lang="scss" scoped>
/* ps-* Klassen kommen global aus product-settings.scss (SettingsView) */
.cp {
  &__synced {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.45);
    margin-right: 8px;
  }
  &__bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 12px;
  }
  &__countries {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 3px;
    border-radius: 14px;
    background: #eef0f3;
  }
  &__cc {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.6);
    cursor: pointer;

    &--on {
      background: #fff;
      color: #282828;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
    }
  }
  &__ccN {
    font-size: 10px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.4);
  }
  &__meta {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
  }
  &__sim {
    margin: 0 !important;
    padding: 0 !important;
  }
  &__config {
    margin-bottom: 14px;
    padding: 12px;
    border-radius: 12px;
    background: #f3f4f7;
  }
  &__configGrid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
    gap: 8px 14px;
  }
  &__configRow {
    display: flex;
    align-items: center;
    gap: 8px;

    &--fee {
      margin-top: 10px;
    }
  }
  &__configCc {
    width: 110px;
    font-size: 12.5px;
    font-weight: 600;
  }
  &__configField {
    max-width: 170px;

    ::v-deep .v-input__slot {
      background: #fff;
      min-height: 34px !important;
    }
  }
  &__simBar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    padding: 10px 14px;
    border-radius: 12px;
    background: #edeafa;
  }
  &__simText {
    flex: 1;
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.75);
  }
  &__testFields {
    display: flex;
    gap: 6px;
  }
  &__testField {
    max-width: 125px;

    ::v-deep .v-input__slot {
      background: #fff;
      min-height: 32px !important;
    }
  }
  &__strike {
    text-decoration: line-through;
    color: rgba(0, 0, 0, 0.4);
    font-size: 12px;
    margin-right: 6px;
  }
  &__margin {
    font-weight: 700;

    &--good {
      color: #2f7d52;
    }
    &--ok {
      color: #a86f00;
    }
    &--low {
      color: #c62828;
    }
  }
  &__pos {
    color: #2f7d52;
    font-weight: 700;
  }
  &__neg {
    color: #c62828;
    font-weight: 700;
  }
  &__muted {
    color: rgba(0, 0, 0, 0.35);
  }
  &__foot {
    padding: 8px 12px;
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.5);
  }
  &__applyCard {
    border-radius: 14px !important;
  }
  &__applyTitle {
    font-size: 16px;
    font-weight: 700;
  }
  &__applyHint {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
  }
  &__applyTable {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;

    th,
    td {
      padding: 6px 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.07);
      text-align: left;
    }
    th {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: rgba(0, 0, 0, 0.5);
    }
  }
  &__applyItem {
    padding: 10px 12px;
    border-radius: 12px;
    background: #f6f7f9;
    margin-bottom: 8px;
  }
  &__applyRow {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    font-size: 13.5px;
  }
  &__applyMain {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
  &__applyTarget {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.5);
  }
  &__applyPrice {
    white-space: nowrap;
  }
  &__applyCountries {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 8px;
  }
  &__applyLabel {
    font-size: 11px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.5);
    margin-right: 4px;
  }
  &__applyCc {
    font-size: 11.5px;
    font-weight: 600;
    padding: 1px 8px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }
  &__applyProducts {
    margin-top: 8px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);

    summary {
      cursor: pointer;
    }
  }
  &__applyProductList {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;

    span {
      padding: 1px 8px;
      border-radius: 999px;
      background: #fff;
      border: 1px solid rgba(0, 0, 0, 0.08);
      font-size: 11px;
    }
  }
  &__applyError {
    display: flex;
    align-items: center;
    margin-top: 10px;
    font-size: 12.5px;
    color: #c62828;
  }
}
</style>
